// recoveryGenerationsClient — 荒らし復旧 UI 第 1 段階「世代を選んで戻す」の HTTP
// クライアント (vandalism-recovery-ui-2026-09-27.md §2「詳細設計」+ 追補「範囲の修正」
// + 「入口を『続きを描く』に統一」)。
//
// サーバ側 3 endpoint (いずれも owner-gated = `Authorization: Bearer <admin_token>`
// 必須、既存 past-canvases 系と同じ `requireRoomAdmin`。3 つとも共通 optional query
// `?sessionId=` を受け付ける — 範囲の修正: 許可集合は「現行セッション ∪ この部屋の
// `room_past_canvases.session_id` (distinct)」、省略時は従来どおり現行セッション):
//   1. `GET  /rooms/:roomId/past-canvases/generations?sessionId=`
//        → `{ sessionId: string | null; generations: { seq, savedAt, isSeedCopy }[] }`
//        (generations は seq 昇順)。許可集合外の sessionId は 404
//        `session_not_found`、空文字は 400 `invalid_session_id` (いずれも既存の
//        404/400 → 'not-found' マッピングにそのまま乗る)。**`sessions[]` は入口
//        統一 (2026-09-28) で廃止**: 別画面での文書切り替えをやめ、過去キャンバス
//        一覧の各行の「続きを描く」がその行の文書の選択画面を開く方式に統一した
//        ため、セッション横断の一覧をこの endpoint が持つ必要が無くなった。
//   2. `GET  /rooms/:roomId/past-canvases/generations/:seq/snapshot-urls?sessionId=`
//        → 既存 `PastCanvasSnapshotUrlsResponse` と同形 (`pastCanvasClient.ts` の
//          `PastCanvasSnapshotUrls` をそのまま再利用し、`composePastCanvasThumbnail`
//          を使い回せるようにする)。404 `generation_not_found` / 503
//          `recovery_generation_unavailable`。
//   3. `POST /rooms/:roomId/past-canvases/generations/:seq?sessionId=`
//        → `{ pastCanvasId: string }` (冪等、キーは (sessionId, seq)。既存行が
//          あれば kind を問わずそれを返す)。client はこの id で既存の
//          `whiteboard.restore-request` をそのまま送る (A 案、プロトコル・seeder
//          は変えない)。
//
// outcome union で返し、期待される失敗 (401/403/404/400/503/network) では throw
// しない (pastCanvasClient.ts と同流儀)。fetch は narrow な shape で注入可能。

import type { FetchFn, PastCanvasClientDeps, PastCanvasSnapshotUrls } from './pastCanvasClient.js';

// ── 世代一覧 (GET /rooms/:roomId/past-canvases/generations) ────────────────

export interface RecoveryGeneration {
  readonly seq: number;
  /** ISO8601 (R2 `LastModified`)。世代の保存時刻。 */
  readonly savedAt: string;
  /**
   * この世代ディレクトリに自前の layer PNG が 1 枚も無い (= 他世代の画素への
   * pointer だけで構成される、典型例は復元直後の seed 世代)。`true` の世代は
   * 直前の世代とサムネイルが見た目上完全に同一になるため、web 側でスライダーから
   * 除く (範囲の修正 §2「同じ中身の世代の重複」)。
   */
  readonly isSeedCopy: boolean;
}

export type FetchRecoveryGenerationsOutcome =
  | {
      readonly outcome: 'ok';
      readonly sessionId: string | null;
      readonly generations: readonly RecoveryGeneration[];
      /**
       * 第 2 段階「人を外して戻す」がこの部屋・この利用者で使えるか。サーバが
       * `true` と返したときだけ true (欠落 / 非 boolean / 古いサーバ = false)。
       * 画面はこの値だけで第 2 段階の UI を出し分ける (他の推測はしない)。
       */
      readonly exclusionAvailable: boolean;
    }
  /** 401/403: 未オーナー (= 非オーナーが叩いた / token 失効)。 */
  | { readonly outcome: 'unauthorized' }
  /** 404/400: 部屋が無い / roomId 不正 / sessionId が許可集合外 / sessionId 不正 (空文字等)。 */
  | { readonly outcome: 'not-found' }
  /** 503: R2 一覧取得失敗 (一時的)。 */
  | { readonly outcome: 'unavailable' }
  /** network / 予期しない status / JSON 破損。 */
  | { readonly outcome: 'error' };

export interface FetchRecoveryGenerationsInput {
  /** dispatcher base URL (`readEnv().dispatcherUrl`). 末尾 `/` は許容。 */
  readonly dispatcherUrl: string;
  readonly roomId: string;
  /** owner の admin_token (Bearer)。無指定は無認証で撃つ → 通常 401 → unauthorized。 */
  readonly adminToken?: string;
  /**
   * 対象セッション (範囲の修正)。省略時はサーバが現行セッションを解決する
   * (後方互換)。許可集合外の値を渡すと `not-found`。
   */
  readonly sessionId?: string;
  readonly signal?: AbortSignal;
}

const authInit = (
  adminToken: string | undefined,
  signal: AbortSignal | undefined,
): { signal?: AbortSignal; headers?: Record<string, string> } => {
  const init: { signal?: AbortSignal; headers?: Record<string, string> } = {};
  if (signal !== undefined) init.signal = signal;
  if (adminToken !== undefined && adminToken.length > 0) {
    init.headers = { Authorization: `Bearer ${adminToken}` };
  }
  return init;
};

const defaultFetchFn: FetchFn = (input, init) => {
  const f = globalThis.fetch as
    | ((url: string, init?: RequestInit) => Promise<Response>)
    | undefined;
  if (f === undefined) throw new Error('globalThis.fetch is not available');
  return f.call(globalThis, input, init as RequestInit | undefined);
};

/** `?sessionId=` を末尾に付与する (未指定なら無変更)。既存 query は無いので単純 `?` 付与でよい。 */
const withSessionQuery = (url: string, sessionId: string | undefined): string =>
  sessionId === undefined ? url : `${url}?sessionId=${encodeURIComponent(sessionId)}`;

const isRecoveryGeneration = (v: unknown): v is RecoveryGeneration => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.seq === 'number' && typeof o.savedAt === 'string' && typeof o.isSeedCopy === 'boolean'
  );
};

export const fetchRecoveryGenerations = async (
  input: FetchRecoveryGenerationsInput,
  deps: PastCanvasClientDeps = {},
): Promise<FetchRecoveryGenerationsOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = withSessionQuery(
    `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/past-canvases/generations`,
    input.sessionId,
  );

  let res: Response;
  try {
    res = await fetchFn(url, authInit(input.adminToken, input.signal));
  } catch {
    return { outcome: 'error' };
  }
  if (res.status === 401 || res.status === 403) return { outcome: 'unauthorized' };
  if (res.status === 404 || res.status === 400) return { outcome: 'not-found' };
  if (res.status === 503) return { outcome: 'unavailable' };
  if (res.status !== 200) return { outcome: 'error' };

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { outcome: 'error' };
  }
  if (typeof raw !== 'object' || raw === null) return { outcome: 'error' };
  const o = raw as Record<string, unknown>;
  if (o.sessionId !== null && typeof o.sessionId !== 'string') return { outcome: 'error' };
  const list = o.generations;
  if (!Array.isArray(list) || !list.every(isRecoveryGeneration)) return { outcome: 'error' };
  return {
    outcome: 'ok',
    sessionId: o.sessionId,
    generations: list,
    exclusionAvailable: o.exclusionAvailable === true,
  };
};

// ── 世代のサムネ署名 (GET .../generations/:seq/snapshot-urls) ──────────────
//
// レスポンス shape は既存 `PastCanvasSnapshotUrls` と同一 (サーバがあえて揃えている
// — §2「既存の snapshot-urls 流儀」)。`composePastCanvasThumbnail` をそのまま使い回す。

export type FetchRecoveryGenerationSnapshotUrlsOutcome =
  | { readonly outcome: 'ok'; readonly urls: PastCanvasSnapshotUrls }
  | { readonly outcome: 'unauthorized' }
  /** 404: 世代がこのセッションに存在しない / セッションが許可集合外 / 部屋にまだ snapshot が無い。 */
  | { readonly outcome: 'not-found' }
  /** 503: 署名 / meta 取得失敗 (一時的)。 */
  | { readonly outcome: 'unavailable' }
  | { readonly outcome: 'error' };

export interface FetchRecoveryGenerationSnapshotUrlsInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly seq: number;
  readonly adminToken?: string;
  /** 対象セッション (範囲の修正)。省略時はサーバが現行セッションを解決する。 */
  readonly sessionId?: string;
  readonly signal?: AbortSignal;
}

const isLayerUrl = (v: unknown): v is { id: number; url: string } => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return typeof o.id === 'number' && typeof o.url === 'string';
};

export const fetchRecoveryGenerationSnapshotUrls = async (
  input: FetchRecoveryGenerationSnapshotUrlsInput,
  deps: PastCanvasClientDeps = {},
): Promise<FetchRecoveryGenerationSnapshotUrlsOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = withSessionQuery(
    `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/past-canvases/generations/${input.seq}/snapshot-urls`,
    input.sessionId,
  );

  let res: Response;
  try {
    res = await fetchFn(url, authInit(input.adminToken, input.signal));
  } catch {
    return { outcome: 'error' };
  }
  if (res.status === 401 || res.status === 403) return { outcome: 'unauthorized' };
  if (res.status === 404 || res.status === 400) return { outcome: 'not-found' };
  if (res.status === 503) return { outcome: 'unavailable' };
  if (res.status !== 200) return { outcome: 'error' };

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { outcome: 'error' };
  }
  if (typeof raw !== 'object' || raw === null) return { outcome: 'error' };
  const o = raw as Record<string, unknown>;
  if (
    typeof o.metaUrl !== 'string' ||
    !Array.isArray(o.layerUrls) ||
    !o.layerUrls.every(isLayerUrl) ||
    typeof o.urlExpiresInSeconds !== 'number'
  ) {
    return { outcome: 'error' };
  }
  return {
    outcome: 'ok',
    urls: {
      metaUrl: o.metaUrl,
      layerUrls: o.layerUrls,
      urlExpiresInSeconds: o.urlExpiresInSeconds,
    },
  };
};

// ── 世代から過去キャンバス行を作る (POST .../generations/:seq) ─────────────

export type CreatePastCanvasFromGenerationOutcome =
  | { readonly outcome: 'ok'; readonly pastCanvasId: string }
  | { readonly outcome: 'unauthorized' }
  /** 404: 世代がこのセッションに存在しない / セッションが許可集合外 / 部屋にまだ snapshot が無い。 */
  | { readonly outcome: 'not-found' }
  /** 503: R2 取得失敗 (一時的)。 */
  | { readonly outcome: 'unavailable' }
  | { readonly outcome: 'error' };

export interface CreatePastCanvasFromGenerationInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly seq: number;
  readonly adminToken?: string;
  /** 対象セッション (範囲の修正)。省略時はサーバが現行セッションを解決する。冪等キーは (sessionId, seq)。 */
  readonly sessionId?: string;
  readonly signal?: AbortSignal;
}

export const createPastCanvasFromGeneration = async (
  input: CreatePastCanvasFromGenerationInput,
  deps: PastCanvasClientDeps = {},
): Promise<CreatePastCanvasFromGenerationOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = withSessionQuery(
    `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/past-canvases/generations/${input.seq}`,
    input.sessionId,
  );

  const init = { ...authInit(input.adminToken, input.signal), method: 'POST' };

  let res: Response;
  try {
    res = await fetchFn(url, init);
  } catch {
    return { outcome: 'error' };
  }
  if (res.status === 401 || res.status === 403) return { outcome: 'unauthorized' };
  if (res.status === 404 || res.status === 400) return { outcome: 'not-found' };
  if (res.status === 503) return { outcome: 'unavailable' };
  if (res.status !== 200) return { outcome: 'error' };

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { outcome: 'error' };
  }
  if (typeof raw !== 'object' || raw === null) return { outcome: 'error' };
  const pastCanvasId = (raw as { pastCanvasId?: unknown }).pastCanvasId;
  if (typeof pastCanvasId !== 'string' || pastCanvasId.length === 0) return { outcome: 'error' };
  return { outcome: 'ok', pastCanvasId };
};
